import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { SotiPopupTrigger } from '@soti/popup';
import { Observable } from 'rxjs';
import { RemoteControlService } from 'xsmodules/remote-control/services/remote-control.service';
import { TaskManagerService } from 'xsmodules/remote-control/services/task-manager.service';
import { TaskManagerState } from 'xsmodules/remote-control/services/taskManagerState';
import { DeviceStateRegistryService, RemoteControlDeviceState } from 'app/modules/remote-control/services/device-state-registry.service';
import { ITabViewState } from 'xsmodules/remote-control/services/rc-tab.service';
import { hd } from '@nx-fe/services';
import { RcContext } from 'app/modules/remote-control-ng/context/rcContext';

@Component({
  selector: 'app-task-manager',
  templateUrl: './task-manager.component.html',
  styleUrls: ['./task-manager.component.scss'],
})
export class TaskManagerComponent implements OnInit {
  private _tabViewState: ITabViewState;
  public set tabViewState(tabViewState: ITabViewState) {
    this._tabViewState = tabViewState;
    this.showComponent(this.tabViewState && this.tabViewState.enabled && this.tabViewState.selected);
  }

  public get tabViewState(): ITabViewState {
    return this._tabViewState;
  }

  public isDropdownOpen = false;
  public taskListSortInfo: hd.ISortInfo;

  public state: TaskManagerState;

  @Input()
  public deviceState: RemoteControlDeviceState;
  public deviceKind: string;
  @ViewChild('killPopupTrigger', { static: false })
  private _popupTrigger: SotiPopupTrigger;

  private remoteControlEndedUnbind: Observable<string>;
  private remoteControlStartedUnbind: Observable<hd.IDeviceModel>;
  // Removing the Linux and Mac as the supported platforms since Task Manager is not being implemented for them.
  private supportedPlatforms: string[] = [
    'WindowsCE',
    'WindowsDesktopLegacy',
    'AndroidPlus',
    'AndroidElm',
    'AndroidKnox',
    'AndroidForWork',
  ];

  constructor(
    private remoteControlService: RemoteControlService,
    private deviceStateRegistry: DeviceStateRegistryService,
    private taskManagerService: TaskManagerService
  ) {}

  ngOnInit() {
    this.remoteControlStartedUnbind = this.remoteControlService.remoteControlStarted$;

    this.state = this.taskManagerService.getState(RcContext.instance.sotiAssistDeviceId) as any;
    this.state.isKillPermission = this.isKillPermission();

    this.taskListSortInfo = this.state.sortInfos[hd.TaskManagerTables.taskList];

    if (this.deviceState) {
      this.deviceKind = this.deviceState.device.kind;
      if (this._tabViewState) {
        this._tabViewState.enabled = this.isSupportedOS(this.deviceKind);
      }
      this.state.setOs(this.deviceKind);
    }

    // TODO: setup scroll bar show/hide
    this.remoteControlEndedUnbind = this.remoteControlService.remoteControlEnded$;

    const taskManagerScrollArea = document.getElementById('taskManagerScrollArea');
    if (taskManagerScrollArea) {
      taskManagerScrollArea.onscroll = this.state.onScroll;
    }

    this.resetState();
    this.showComponent();
  }

  public sortBy = (column: string) => {
    this.state.sortTableBy(column, this.state.taskList, this.taskListSortInfo);
  };

  public getState = (): TaskManagerState => this.state;

  public getTaskList = (): hd.IRcTask[] => this.state.getTaskList();

  public getDetails = (task: hd.IRcTask): void => {
    this.state.selectedTask = task;
    this.state.inDetailsView = true;
    this.state.getTaskDetails(this.state.selectedTask.pid);
  };

  public onScroll = () => {
    this.state.onScroll();
  };

  public isRefreshIntervalManual = (): boolean => this.getState().refreshSpeed === 'manual';

  public closePopup($event?: MouseEvent): void {
    // $event && $event.stopImmediatePropagation();
    this._popupTrigger.close();
  }

  public killProcess($event?: MouseEvent): void {
    this.state.sendKillProcess();
    this.closePopup($event);
  }

  public isKillPermission(): boolean {
    if (this.deviceState && this.deviceState.device) {
      const devicePermissions = this.deviceStateRegistry.getPermissions(this.deviceState.device.deviceId);
      return (
        devicePermissions &&
        devicePermissions.permissions &&
        (devicePermissions.permissions & hd.RemoteControlPermissions.RemoteControlManageTasks) !== 0
      );
    }
  }

  private isSupportedOS = (osType: string): boolean => this.supportedPlatforms.indexOf(osType) >= 0;

  private showComponent = (isEnabled: boolean = true) => {
    if (isEnabled) {
      this.state.init();
    }
  };

  private resetState(): void {
    this.state.selectedTask = null;
    this.state.inDetailsView = false;
  }
}
